import { useState } from 'react';
import { track, EVENTS } from '@/lib/mixpanel';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { IconLoader, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { GoogleSignInButton } from '@/components/common/GoogleSignInButton';

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignInSuccess?: (user?: any) => void;
}

export default function SignInModal({ isOpen, onClose, onSignInSuccess }: SignInModalProps) {
  const { signInWithGoogleOAuth, signInWithApple } = useAuth();
  const { t, i18n } = useTranslation();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);

  const handleGoogleButtonSignIn = (user: any) => {
    console.log('Google button sign-in successful, user:', user);
    setIsGoogleLoading(true);
    onSignInSuccess?.(user);
    onClose();
  };

  const handleGoogleButtonError = (error: any) => {
    console.error('Google button sign-in error:', error);
    setIsGoogleLoading(false);
    // Could show a toast notification here if needed
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      track(EVENTS.INITIATE_SIGN_IN, { provider: 'google', context: 'modal' });

      // // Try One Tap first
      // try {
      //   const signedInUser = await signInWithGoogleOneTap();
      //   onSignInSuccess?.(signedInUser);
      //   onClose();
      // } catch (oneTapError) {
      //   console.log('One Tap failed, falling back to OAuth redirect:', oneTapError);
      //   // Fallback to OAuth redirect
      //   setIsGoogleLoading(false);
      //   await signInWithGoogleOAuth();
      //   // Note: OAuth redirect will navigate away, so no need to call onSignInSuccess here
      // }


      await signInWithGoogleOAuth();

    } catch (error) {
      console.error('Error signing in with Google:', error);
      setIsGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setIsAppleLoading(true);
    try {
      track(EVENTS.INITIATE_SIGN_IN, { provider: 'apple', context: 'modal' });
      await signInWithApple();
      onSignInSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error signing in with Apple:', error);
    } finally {
      setIsAppleLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsGoogleLoading(false);
      setIsAppleLoading(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent hideClose className="sm:max-w-md p-0 border-0 bg-transparent shadow-none max-w-[95vw]">
        <div className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Header with close button */}
          <DialogHeader className="relative p-6 pb-4 border-b border-slate-100">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <IconX className="w-5 h-5 text-slate-400" />
            </button>

            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/brands/clarymind-logo_logotype-app-transparent-light-outline.svg"
                alt="Clarymind"
                className="h-8 w-auto"
              />
            </div>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 py-6">
            <div className="w-full max-w-sm mx-auto space-y-4">
              <div className="flex flex-col items-center space-y-4 text-center">
                <h1 className="text-lg font-semibold text-slate-900">
                  {t('auth.signIn')}
                </h1>
                {isGoogleLoading && (
                  <IconLoader className="h-5 w-5 animate-spin mr-2" />
                )}
              </div>

              <div className="space-y-2 items-center flex flex-col">

                {/* New Google Sign In Button */}
                <GoogleSignInButton
                  onSuccess={handleGoogleButtonSignIn}
                  onError={handleGoogleButtonError}
                  className="mx-auto"
                  buttonConfig={{
                    size: 'large',
                    width: 320,
                    locale: i18n.language
                  }}
                />

                {/* <Button
                  variant="outline"
                  className="w-full py-5 rounded-lg"
                  onClick={handleGoogleSignIn}
                  disabled={isGoogleLoading}
                >
                  {isGoogleLoading ? (
                    <IconLoader className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <img
                      src="/brands/google.svg"
                      alt="Google"
                      className="w-5 h-5 mr-2"
                    />
                  )}
                  {t('auth.signInWithGoogle')}
                </Button> */}

                {/* Uncomment when Apple sign-in is enabled */}
                {/* <Button
                  variant="outline"
                  className="w-full py-5 rounded-lg"
                  onClick={handleAppleSignIn}
                  disabled={isAppleLoading}
                >
                  {isAppleLoading ? (
                    <IconLoader className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <img
                      src="/brands/apple.svg"
                      alt="Apple"
                      className="w-5 h-5 mr-2"
                    />
                  )}
                  {t('auth.signInWithApple')}
                </Button> */}
              </div>

              <div className="space-y-6 pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-slate-200"></div>
                  <h2 className="text-xs text-slate-400 px-3 text-center">
                    {t('auth.signInWhy')}
                  </h2>
                  <div className="h-px flex-1 bg-slate-200"></div>
                </div>

                <p className="text-sm text-slate-600 text-center px-3">
                  {t('auth.signInHint')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
